import {
  ApiFixedMultiplier,
  ApiMutualFundAsset,
  ApiVariableMultiplier
} from 'api/_legacy/generated-api'
import { isUpperCase } from 'utilities/string'

import { ValidatorRules } from '../../../tradebuilder-v2/util/validator'
import { EditAssetsState } from '../edit-asset-slice'

export const editAssetRules: ValidatorRules<EditAssetsState> = {
  asset_name: [
    (state: EditAssetsState) => ({
      isValid: state.selectedAsset.asset.name.length > 0,
      message: 'Asset must have a name'
    })
  ],
  isin: [
    (state: EditAssetsState) => {
      const isinCode = (state.selectedAsset.asset as any).isinCode as string | undefined
      if (state.isNew) {
        return {
          isValid: (isinCode?.length ?? 0) === 12,
          message: 'Asset must have an ISIN containing 12 characters'
        }
      }
      return {
        isValid: isinCode === state.selectedAsset.asset.assetId,
        message: 'ISIN of an existing asset cannot be changed'
      }
    }
  ],
  asset_class: [
    (state: EditAssetsState) => ({
      isValid: state.selectedAsset.asset.assetClass.length > 0,
      message: 'Asset must have an asset class'
    })
  ],
  fund_manager: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid: asset.fundManager && asset.fundManager.length > 0,
        message: 'Asset must have a fund manager name'
      }
    }
  ],
  fund_mgr_cpty: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid: asset.externalIdentifiers.fundManagerCounterpartyId.length > 0,
        message: 'Asset must have a fund manager counterparty Id'
      }
    }
  ],
  manco_code: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid:
          asset.externalIdentifiers.managementCompanyCode.length > 0 &&
          isUpperCase(asset.externalIdentifiers.managementCompanyCode),
        message: 'Asset must have a ManCo code which is all upper case'
      }
    }
  ],
  product_code: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid:
          asset.externalIdentifiers.productCode.length > 0 &&
          isUpperCase(asset.externalIdentifiers.productCode),
        message: 'Asset must have a product code which is all upper case'
      }
    }
  ],
  fund_code: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid:
          asset.externalIdentifiers.fundCode.length > 0 &&
          isUpperCase(asset.externalIdentifiers.fundCode),
        message: 'Asset must have a fund code which is all upper case'
      }
    }
  ],
  account_number: [
    (state: EditAssetsState) => {
      if (state.selectedAsset.asset.type === 'SegregatedPortfolio') {
        return {
          isValid: true,
          message: ''
        }
      }
      const asset = state.selectedAsset.asset as unknown as ApiMutualFundAsset
      return {
        isValid: asset.externalIdentifiers.accountNumber.length > 0,
        message: 'Asset must have an account number'
      }
    }
  ],
  max_lev_ratio: [
    (state: EditAssetsState) => ({
      isValid:
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? true
          : state.selectedAsset.asset.maxLeverageRatio > 0 &&
            state.selectedAsset.asset.maxLeverageRatio < 10,
      message: 'Max Leverage Ratio must be greater than 0 and less than 10'
    })
  ],
  short_lambda: [
    (state: EditAssetsState) => ({
      isValid:
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? true
          : state.selectedAsset.asset.multiplier.shortLambda > 0 &&
            state.selectedAsset.asset.multiplier.shortLambda < 10,
      message: 'Short Lambda must be greater than 0 and less than 10'
    })
  ],
  long_lambda: [
    (state: EditAssetsState) => ({
      isValid:
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? true
          : state.selectedAsset.asset.multiplier.longLambda > 0 &&
            state.selectedAsset.asset.multiplier.longLambda < 10,
      message: 'Long Lambda must be greater than 0 and less than 10'
    })
  ],
  fixed_multiplier: [
    (state: EditAssetsState) => {
      const fixedMultiplier =
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? null
          : (state.selectedAsset.asset.multiplier as ApiFixedMultiplier)
      return {
        isValid:
          state.selectedAsset.asset.protectionClassification === 'Protection'
            ? true
            : state.selectedAsset.asset.multiplier.multiplierType === 'Variable' ||
              (fixedMultiplier.multiplier > 0 && fixedMultiplier.multiplier < 15),
        message: 'Fixed Multiplier must be greater than 0 and less than 15'
      }
    }
  ],
  standard_multiplier: [
    (state: EditAssetsState) => {
      const variableMultiplier =
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? null
          : (state.selectedAsset.asset.multiplier as ApiVariableMultiplier)
      return {
        isValid:
          state.selectedAsset.asset.protectionClassification === 'Protection'
            ? true
            : state.selectedAsset.asset.multiplier.multiplierType === 'Fixed' ||
              (variableMultiplier.standardMultiplier > 0 &&
                variableMultiplier.standardMultiplier < 15),
        message: 'Standard Multiplier must be greater than 0 and less than 15'
      }
    }
  ],
  reduced_multiplier: [
    (state: EditAssetsState) => {
      const variableMultiplier =
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? null
          : (state.selectedAsset.asset.multiplier as ApiVariableMultiplier)
      return {
        isValid:
          state.selectedAsset.asset.protectionClassification === 'Protection'
            ? true
            : state.selectedAsset.asset.multiplier.multiplierType === 'Fixed' ||
              (variableMultiplier.reducedMultiplier > 0 &&
                variableMultiplier.reducedMultiplier < 15),
        message: 'Reduced Multiplier must be greater than 0 and less than 15'
      }
    }
  ],
  reduced_vs_std_multiplier: [
    (state: EditAssetsState) => {
      const variableMultiplier =
        state.selectedAsset.asset.protectionClassification === 'Protection'
          ? null
          : (state.selectedAsset.asset.multiplier as ApiVariableMultiplier)
      return {
        isValid:
          state.selectedAsset.asset.protectionClassification === 'Protection'
            ? true
            : state.selectedAsset.asset.multiplier.multiplierType === 'Fixed' ||
              variableMultiplier.reducedMultiplier < variableMultiplier.standardMultiplier,
        message: 'Reduced Multiplier must be less than Standard Multiplier'
      }
    }
  ]
}
